import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createOrder } from '../../store/CommonSlice';
import { AppDispatch } from '../../store/store';
// import { clearCart, setToLocalStorage } from '../../store/UserSlice';
import { updateLocalQuantities } from '../../store/ProductSlice';
import { PATHS } from '../../constants/routes';
import { AddressStruct, AppliedPromo, ProductCartItem } from '../../types/common';
import { pushEcommerceCheckout } from '../../lib/cartPage/analytics';

type CommonErrorState = {
    isError?: boolean;
    [k: string]: any;
};
type CheckoutInput = {
    name: string;
    phone: string;
    address: string;
    orderComment: string;
};
type EffectiveDeliveryMethod = 'pickup' | 'dpd' | 'dtl';
type UseCheckoutSubmitArgs = {
    dispatch: AppDispatch;
    navigate: ReturnType<typeof useNavigate>;
    cartProducts: ProductCartItem[];
    payableTotal: number;
    weight: number;
    profit: number;
    quantity: number;
    authUser: any;
    input: CheckoutInput;
    selectedDeliveryAddress: CheckoutAddressContainer | null;
    selectedDiscount: 'birthday' | 'bonus' | null;
    bonusEligible: number | null;
    originalTotal: number;
    payWithSBP: boolean;
    payWithInstallments: boolean;
    appliedPromo: AppliedPromo | null;
    effectiveDeliveryMethod: EffectiveDeliveryMethod;
    effectiveShippingCost: number | null;
    commonError: CommonErrorState | null;
    isLoading: boolean;
    resetForm: () => void;
};
type CheckoutAddressContainer = {
    raw: string;
    struct: AddressStruct | null;
};

function safeNumber(v: unknown, fallback = 0): number {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

function normalizeStr(v: unknown): string {
    return typeof v === 'string' ? v.trim() : '';
}
// function normalizeRawKey(v: string): string {
//     return normalizeStr(v).replace(/\s+/g, ' ').toLowerCase();
// }
function splitHumanName(value: unknown): { lastName: string; firstName: string; middleName: string } {
    const parts = normalizeStr(value).split(/\s+/).filter(Boolean);
    return {
        lastName: parts[0] ?? '',
        firstName: parts[1] ?? '',
        middleName: parts.slice(2).join(' '),
    };
}

export function useCheckoutSubmit({
    dispatch,
    navigate,
    cartProducts,
    payableTotal,
    weight,
    profit,
    quantity,
    authUser,
    input,
    selectedDeliveryAddress,
    selectedDiscount,
    bonusEligible,
    originalTotal,
    payWithSBP,
    payWithInstallments,
    appliedPromo,
    effectiveDeliveryMethod,
    effectiveShippingCost,
    commonError,
    isLoading,
    resetForm,
}: UseCheckoutSubmitArgs) {
    const [showOverQuantityModal, setShowOverQuantityModal] = useState(false);
    const [overQuantityProducts, setOverQuantityProducts] = useState<ProductCartItem[]>([]);
    const exceedsProducts = useMemo(() => {
        return cartProducts.filter((i: any) => safeNumber(i?.quantity, 0) > safeNumber(i?.availableQuantity, 0));
    }, [cartProducts]);
    const hasExceeds = exceedsProducts.length > 0;
    const openOverQtyModal = useCallback(() => {
        setOverQuantityProducts(exceedsProducts);
        setShowOverQuantityModal(true);
    }, [exceedsProducts]);
    const closeOverQtyModal = useCallback(() => {
        setShowOverQuantityModal(false);
    }, []);
    const lastSubmitUserRef = useRef(authUser);
    useEffect(() => {
        lastSubmitUserRef.current = authUser;
    }, [authUser]);
    const buildOrderPayload = useCallback(
        (opts: { totalPrice: number; originalTotalValue: number }, authUserOverride?: UseCheckoutSubmitArgs['authUser']) => {
            const sourceUser = authUserOverride ?? authUser;
            const orderComment = input.orderComment;
            const inputNameParts = splitHumanName(input.name);
            const authFirstName = normalizeStr(sourceUser?.firstName);
            const authLastName = normalizeStr(sourceUser?.lastName);
            const authMiddleName = normalizeStr(sourceUser?.middleName);
            const resolvedFirstName = authFirstName || inputNameParts.firstName;
            const resolvedLastName = authLastName || inputNameParts.lastName;
            const resolvedMiddleName = authMiddleName || inputNameParts.middleName;
            const resolvedName = [resolvedLastName, resolvedFirstName, resolvedMiddleName].filter(Boolean).join(' ').trim() || normalizeStr(input.name);
            const resolvedPhone = normalizeStr(sourceUser?.phoneNumber) || normalizeStr(input.phone);
            const resolvedEmail = normalizeStr(sourceUser?.email);
            const resolvedAddress = effectiveDeliveryMethod === 'pickup' ? null : selectedDeliveryAddress;
            return {
                user: {
                    name: resolvedName,
                    phone: resolvedPhone,
                    ...(resolvedEmail ? { email: resolvedEmail } : {}),
                    ...(resolvedFirstName ? { firstName: resolvedFirstName } : {}),
                    ...(resolvedLastName ? { lastName: resolvedLastName } : {}),
                    ...(resolvedMiddleName ? { middleName: resolvedMiddleName } : {}),
                    address: resolvedAddress,
                },
                cart: cartProducts,
                timestamp: Date.now(),
                totalPrice: opts.totalPrice,
                totalWeight: weight,
                totalDiscount: profit,
                totalQuantity: quantity,
                userID: sourceUser?._id,
                orderComment,
                birthdayDiscount: selectedDiscount === 'birthday',
                bonusPointsUsed: selectedDiscount === 'bonus' && bonusEligible ? bonusEligible : 0,
                originalTotal: opts.originalTotalValue,
                payWithSBP,
                payWithInstallments,
                promoCode: appliedPromo
                    ? { code: appliedPromo.code, discountType: appliedPromo.discountType, discountValue: appliedPromo.discountValue }
                    : undefined,
                deliveryMethod: effectiveDeliveryMethod,
                shippingCost: effectiveDeliveryMethod !== 'pickup' && effectiveShippingCost != null ? +effectiveShippingCost.toFixed(2) : 0,
            };
        },
        [
            input,
            authUser,
            cartProducts,
            weight,
            profit,
            quantity,
            selectedDeliveryAddress,
            selectedDiscount,
            bonusEligible,
            payWithSBP,
            payWithInstallments,
            appliedPromo,
            effectiveDeliveryMethod,
            effectiveShippingCost,
        ],
    );

    const navigateToSuccess = useCallback(
        (orderNumber: string) => {
            navigate(`${PATHS.cart}/${PATHS.success}`, { state: { orderNumber } });
        },
        [navigate],
    );

    const submitOrder = useCallback(
        async (authUserOverride?: UseCheckoutSubmitArgs['authUser']) => {
            if (hasExceeds) {
                openOverQtyModal();
                return;
            }
            // DPD: ждём расчёт; DTL - не ждём (там 0)
            if (effectiveDeliveryMethod === 'dpd' && effectiveShippingCost === null) {
                alert('Подождите, идёт расчёт стоимости доставки');
                return;
            }

            const sourceUser = authUserOverride ?? authUser;
            lastSubmitUserRef.current = sourceUser;

            pushEcommerceCheckout(cartProducts, payableTotal, effectiveShippingCost, appliedPromo);
            const order = buildOrderPayload(
                {
                    totalPrice: +payableTotal.toFixed(2),
                    originalTotalValue: +originalTotal.toFixed(2),
                },
                sourceUser,
            );
            const res: any = await dispatch(createOrder(order));
            if (res?.error) {
                console.error('Ошибка при создании заказа:', res.error);
                return;
            }

            // чистим корзину сразу после успешного dispatch(createOrder)
            // dispatch(clearCart());
            // dispatch(setToLocalStorage('cart'));
            // если есть confirmationUrl - дальше не идём
            if (res?.payload?.confirmationUrl) return;
            if (!commonError?.isError && !isLoading && res?.payload) {
                navigateToSuccess(res.payload.orderNumber);
                dispatch(updateLocalQuantities(cartProducts));
                resetForm();
            }
        },
        [
            hasExceeds,
            openOverQtyModal,
            effectiveDeliveryMethod,
            effectiveShippingCost,
            cartProducts,
            payableTotal,
            appliedPromo,
            buildOrderPayload,
            originalTotal,
            dispatch,
            authUser,
            commonError?.isError,
            isLoading,
            navigateToSuccess,
            resetForm,
        ],
    );

    const confirmOverQuantity = useCallback(async () => {
        setShowOverQuantityModal(false);
        pushEcommerceCheckout(cartProducts, payableTotal, effectiveShippingCost, appliedPromo);
        const order = buildOrderPayload(
            {
                totalPrice: payableTotal,
                originalTotalValue: originalTotal,
            },
            lastSubmitUserRef.current,
        );
        const res: any = await dispatch(createOrder(order));
        if (!commonError?.isError && !isLoading && res?.payload) {
            navigateToSuccess(res.payload.orderNumber);
            dispatch(updateLocalQuantities(cartProducts));
            // dispatch(clearCart());
            // dispatch(setToLocalStorage('cart'));
            resetForm();
        }
    }, [
        cartProducts,
        payableTotal,
        effectiveShippingCost,
        appliedPromo,
        buildOrderPayload,
        originalTotal,
        dispatch,
        commonError?.isError,
        isLoading,
        navigateToSuccess,
        resetForm,
    ]);
    return {
        submitOrder,
        overQuantity: {
            show: showOverQuantityModal,
            products: overQuantityProducts,
            close: closeOverQtyModal,
            confirm: confirmOverQuantity,
        },
    };
}
